'use client';

import { useMemo, useState } from 'react';
import ProductCardListing from '@/components/products/product-card/product-card-listing';
import ProductPagination from '@/components/products/product-pagination.tsx/product-pagination';
import ProductsSortBy from '@/components/products/products-sort-by/products-sort-by';
import { Link } from '@/components/ui';
import { useCategoriesContext } from '@/hooks/use-categories';
import useCategoryTrees from '@/hooks/use-category-trees';
import useCoreConfig from '@/hooks/use-core-config';
import { usePriceFilterContext } from '@/hooks/use-price-filter-context';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import { useProductsContext } from '@/hooks/use-products';
import useResetProductListingFiltersOnNavigate from '@/hooks/use-reset-product-listing-filters-on-navigate';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { CategoryTree } from '@/types/category/category-get-tree';
import { SortByItemKeys } from '@/types/common/sort-by-item-keys';
import { applyFilters, paginateProducts, sortProducts } from '@/utils/filtered-products';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';
import { getProductListingAppliedFilters } from '@/utils/product-listing-search-params';
import { buildStudioProductsCategoryPath } from '@/utils/studio-preview-locale-url';
import NoNata from '../common/no-data/no-data';

const AllProductsPageContent = () => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>() ?? {};
  const { features } = useCoreConfig();
  const { isAuthenticated } = useKeycloak();
  const products = useProductsContext();
  const categoryTrees = useCategoryTrees();
  const categories = useCategoriesContext();
  const priceFilterCtx = usePriceFilterContext();

  const searchParams = useProductListingSearchParams();
  useResetProductListingFiltersOnNavigate();
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
  const sortBy = (searchParams.get('sort-by') as SortByItemKeys) ?? SortByItemKeys.NONE;

  const sortedProducts = sortProducts(products.items, sortBy, priceFilterCtx);

  const appliedFilters = getProductListingAppliedFilters(searchParams, { isStudioPreview });

  const finalFilteredProducts = applyFilters(sortedProducts, appliedFilters, priceFilterCtx);

  const { paginatedProducts: currentProducts, totalPages } = paginateProducts(
    finalFilteredProducts,
    currentPage,
    25,
  );

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const categoriesById = useMemo(
    () => new Set(categories.items.map(category => category.id)),
    [categories.items],
  );

  const normalizedCategoryTrees = useMemo(() => {
    const normalizeNodes = (nodes: CategoryTree[]): CategoryTree[] => {
      return nodes
        .filter(node => categoriesById.has(node.id))
        .map(node => ({
          ...node,
          subCategories: normalizeNodes(node.subCategories ?? []),
        }));
    };

    return normalizeNodes(categoryTrees).sort((a, b) => a.name.localeCompare(b.name));
  }, [categoryTrees, categoriesById]);

  const renderCategoryTree = (nodes: CategoryTree[], level = 0) => {
    return (
      <ul className="space-y-1">
        {nodes.map(node => {
          const hasChildren = (node.subCategories?.length ?? 0) > 0;
          const isExpanded = expandedCategories.has(node.id);

          return (
            <li key={node.id}>
              <div
                className="flex items-center gap-1"
                style={{ paddingLeft: level > 0 ? `${level * 12}px` : undefined }}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => toggleCategory(node.id)}
                    className="flex h-5 w-5 items-center justify-center rounded text-xs hover:bg-gray-100"
                    aria-label={isExpanded ? 'Collapse category' : 'Expand category'}
                  >
                    {isExpanded ? '-' : '+'}
                  </button>
                ) : (
                  <span className="inline-block h-5 w-5" aria-hidden="true" />
                )}

                <Link
                  href={
                    isStudioPreview
                      ? buildStudioProductsCategoryPath(node.meta.url)
                      : `/products/${node.meta.url}`
                  }
                  className="text-foreground hover:underline"
                >
                  {node.name}
                </Link>
              </div>

              {hasChildren && isExpanded && (
                <div className="mt-1 overflow-hidden">
                  {renderCategoryTree(node.subCategories, level + 1)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const productsPageLabel = t?.products.productsPage ?? 'Products';

  return (
    <div>
      <h1 className="mb-6 text-4xl font-semibold" data-testid="all-products-page__heading">
        {productsPageLabel}
      </h1>

      <div className="flex w-full gap-4" data-testid="all-products-page__content">
        <div className="hidden w-64 @lg:block" data-testid="all-products-page__categories">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{t?.products.category}</h2>
            {renderCategoryTree(normalizedCategoryTrees)}
          </div>
        </div>

        <div className="w-full @lg:overflow-hidden">
          <div
            className="mb-4 flex items-center gap-4 @lg:pt-2"
            data-testid="all-products-page__sort-filters"
          >
            <div className="w-full @lg:ml-4">
              <div className="flex w-full items-center justify-between">
                {((!isAuthenticated && features.priceForGuests) || isAuthenticated) && (
                  <ProductsSortBy />
                )}
                <span className="ml-auto text-sm text-gray-700">
                  {finalFilteredProducts.length === 1
                    ? t?.products.itemsFound.one
                    : t?.products.itemsFound.other.replace(
                        '{{count}}',
                        finalFilteredProducts.length.toString(),
                      )}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-4 block @lg:hidden" data-testid="all-products-page__categories-mobile">
            <h2 className="mb-2 text-base font-semibold">{t?.products.category}</h2>
            <div className="flex flex-wrap gap-2">
              {categories.items
                .filter(category => Boolean(category?.meta?.url))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(category => (
                  <Link
                    key={category.id}
                    href={`/products/${category.meta.url}`}
                    className="rounded-full border px-3 py-1 text-sm hover:bg-gray-50"
                  >
                    {category.name}
                  </Link>
                ))}
            </div>
          </div>

          {currentProducts.length !== 0 ? (
            <>
              <div
                className="grid grid-cols-2 gap-4 @md:grid-cols-3 @lg:grid-cols-3 @xl:grid-cols-4"
                data-testid="all-products-page__product-grid"
              >
                {currentProducts.map((product, index) => (
                  <ProductCardListing
                    key={product.id || product.sku || index}
                    product={mapProductToSimpleProductInfo(product)}
                    priority={index === 0}
                  />
                ))}
              </div>
              <div className="mt-8 flex justify-center" data-testid="all-products-page__pagination">
                <ProductPagination totalPages={totalPages} />
              </div>
            </>
          ) : (
            <NoNata
              label={t?.products.noProductFound ?? ''}
              className="my-52 text-black"
              data-testid="all-products-page__no-products"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AllProductsPageContent;
